import { MapPin, Phone, Mail, MessageCircle, BriefcaseBusiness, Download } from "lucide-react";
import { FadeUp, SectionHead } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { COMPANY, whatsappLink } from "@/data/site";

export default function Contact() {
  return (
    <>
      <section className="pt-40 pb-20 bg-ink" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">Contact</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              Talk to the<br /><em className="text-gold italic">house of Waghe.</em>
            </h1>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 sm:pb-32 bg-ink" data-testid="contact-body">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-5">
            <FadeUp className="bg-coal border border-white/10 rounded-sm p-7" data-testid="contact-address-card">
              <div className="flex gap-4">
                <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Registered Office &amp; Works</p>
                  <p className="mt-2 text-sm text-cream leading-relaxed">{COMPANY.name}<br />{COMPANY.address}</p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.08} className="bg-coal border border-white/10 rounded-sm p-7" data-testid="contact-phone-card">
              <div className="flex gap-4">
                <Phone className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Call the Sales Desk</p>
                  <div className="mt-2 space-y-1.5">
                    {COMPANY.phones.map((p, i) => (
                      <a key={p} href={`tel:${COMPANY.phoneRaw[i]}`} data-testid={`contact-phone-${i}`} className="block text-sm text-cream hover:text-gold transition-colors duration-300">{p}</a>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.16} className="bg-coal border border-white/10 rounded-sm p-7" data-testid="contact-email-card">
              <div className="flex gap-4">
                <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Write to Us</p>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="mt-2 block text-sm text-cream hover:text-gold transition-colors duration-300">{COMPANY.email}</a>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.24}>
              <a
                href={whatsappLink("Namaste Waghe & Sons! I have a question.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="w-full inline-flex items-center justify-center gap-2.5 bg-[#1ebe5d] text-white text-sm font-semibold uppercase tracking-[0.14em] px-7 py-4 rounded-sm transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#22d367] hover:shadow-[0_14px_36px_rgba(30,190,93,0.35)]"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp — {COMPANY.phones[0]}
              </a>
            </FadeUp>

            <FadeUp delay={0.3} className="border border-white/10 rounded-sm overflow-hidden" data-testid="contact-map">
              <iframe
                title="Waghe & Sons Food Pvt. Ltd. — Barwani location map"
                src="https://maps.google.com/maps?q=Jawahar%20Marg%2C%20Guruwa%20Mohalla%2C%20Barwani%2C%20Madhya%20Pradesh%20451551%2C%20India&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-64 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
            </FadeUp>

            <FadeUp delay={0.38} className="bg-coal border border-gold/25 rounded-sm p-6" data-testid="visiting-card-block">
              <p className="text-[10px] uppercase tracking-[0.24em] text-gold mb-4">Our Visiting Card</p>
              <img
                src="/assets/visiting-card.jpg"
                alt="Waghe & Sons Foods Private Limited — official visiting card"
                className="w-full h-auto rounded-sm border border-white/10"
                data-testid="visiting-card-preview"
              />
              <a
                href="/assets/visiting-card.jpg"
                download="Waghe-and-Sons-Visiting-Card.jpg"
                data-testid="download-visiting-card-button"
                className="mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-6 py-4 rounded-sm transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light hover:shadow-[0_12px_32px_rgba(212,175,55,0.35)]"
              >
                <Download className="w-4 h-4" /> Download Visiting Card
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="bg-coal border border-white/10 rounded-sm p-7 sm:p-10">
              <h2 className="font-display text-3xl text-cream">Send us a message</h2>
              <p className="mt-2 text-sm text-zinc-400 font-light mb-8">Product questions, bulk orders, feedback — we read everything.</p>
              <InquiryForm type="contact" />
            </div>
          </FadeUp>
        </div>
      </section>

      <section id="careers" className="py-24 sm:py-32 bg-coal border-t border-white/10" data-testid="careers-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHead
              testId="careers-head"
              overline="Careers · Join 50+ Skilled Hands"
              title="Build your career with Waghe & Sons."
              description="We hire across production, quality, packing, sales and logistics at our Barwani facility. If you take pride in honest work, there's a place for you in this house."
            />
            <FadeUp delay={0.22} className="mt-8 flex items-center gap-4 text-sm text-zinc-400">
              <BriefcaseBusiness className="w-5 h-5 text-gold" strokeWidth={1.5} />
              <span>Production · Quality · Packing · Sales · Logistics</span>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="bg-ink border border-white/10 rounded-sm p-7 sm:p-10">
              <InquiryForm type="career" />
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
